import React, { useCallback, useContext,useMemo, useState } from "react";

import { MenuContentPopup } from "../MenuContentPopup";
import { MenuHeader } from "../MenuHeader";
import { MenuTransitionContainer } from "../MenuTransitionContainer";
import { MobileMenuContext } from "../MobileMenuContext";
import { ActiveIdPath } from "../types";

import { CategoriesMenu } from "./components/CategoriesMenu";
import { LinksMenu } from "./components/LinksMenu";
import { SubCategoriesMenu } from "./components/SubCategoriesMenu";

export type CategoryMenuProps = {
  isOpen: boolean;
  toggleMenu: () => void;
};

export const CategoryMenu = ({
  isOpen = false,
  toggleMenu,
}: CategoryMenuProps) => {
  const { shops = [] } = useContext(MobileMenuContext);
  const [currentIdPath, setCurrentIdPath] = useState<ActiveIdPath>([]);
  const [transition, setTransition] = useState<{
    direction: "left" | "right";
    active: boolean;
  }>({
    direction: "left",
    active: false,
  });

  const currentMenuLevel = useMemo(
    () => currentIdPath.length + 1,
    [currentIdPath]
  );

  const activeItemName = useMemo<string | undefined>(() => {
    if (currentMenuLevel === 2) {
      return shops.find(({ id }) => id === currentIdPath[0])?.name;
    }

    if (currentMenuLevel === 3) {
      return shops
        .find(({ id }) => id === currentIdPath[0])
        ?.shopNavigationGroups.find(({ id }) => id === currentIdPath[1])?.name;
    }
  }, [currentIdPath, currentMenuLevel, shops]);

  const handleMenuClick = useCallback((idPath: ActiveIdPath) => {
    setTransition({ direction: "left", active: true });
    setCurrentIdPath(idPath);
  }, []);

  const handleMenuBackAction = useCallback(() => {
    setTransition({ direction: "right", active: true });
    setCurrentIdPath(currentIdPath.slice(0, currentIdPath.length - 1));
  }, [currentIdPath]);

  const handleTransitionEnd = useCallback(() => {
    setTransition((prevState) => ({
      ...prevState,
      active: false,
    }));
  }, []);

  return (
    <MenuContentPopup isOpen={isOpen}>
      <MenuHeader
        aria-label="Mobile category menu header"
        title={activeItemName || "Categories"}
        level={currentMenuLevel}
        color="secondary"
        closeMenu={toggleMenu}
        onBackButtonClick={handleMenuBackAction}
      />
      <MenuTransitionContainer
        aria-label="Mobile category menu"
        active={transition.active}
        direction={transition.direction}
        onAnimationEnd={handleTransitionEnd}
      >
        {currentMenuLevel === 1 && (
          <CategoriesMenu onItemClick={handleMenuClick} />
        )}
        {currentMenuLevel === 2 && (
          <SubCategoriesMenu
            idPath={currentIdPath}
            onItemClick={handleMenuClick}
          />
        )}
        {currentMenuLevel === 3 && <LinksMenu idPath={currentIdPath} />}
      </MenuTransitionContainer>
    </MenuContentPopup>
  );
};
